import { useEffect, useState } from "react";
import { Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const quando = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** Chat temporanea su una pratica segnalata: tutti i partecipanti possono scrivere ed eliminarla. */
export function ChatSegnalazione({ chatId, notifica, me, onClose, onChange }) {
  const [chat, setChat] = useState(null);
  const [errore, setErrore] = useState("");
  const [testo, setTesto] = useState("");
  const [invio, setInvio] = useState(false);
  const [conferma, setConferma] = useState(false);

  const carica = () => api.get(`/notifiche/chat/${chatId}`).then((r) => { setChat(r.data); setErrore(""); })
    .catch((e) => setErrore(apiError(e)));
  useEffect(() => { carica(); }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  const invia = async () => {
    if (!testo.trim()) return;
    setInvio(true);
    try {
      const r = await api.post(`/notifiche/chat/${chatId}/messaggio`, { testo });
      setChat(r.data); setTesto("");
      onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setInvio(false); }
  };
  const elimina = async () => {
    try {
      await api.delete(`/notifiche/chat/${chatId}`);
      toast.success("Chat eliminata definitivamente");
      onChange?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50" data-testid={`chat-${chatId}`}
      onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
        <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500">
          Chat · pratica {notifica?.external_id || chat?.external_id}
        </span>
        <div className="flex items-center gap-2">
          {conferma ? (
            <span className="flex items-center gap-1 text-[11px]">
              <span className="text-slate-600">Eliminare per tutti?</span>
              <button type="button" onClick={elimina} data-testid={`chat-elimina-conferma-${chatId}`}
                className="px-2 py-0.5 rounded bg-[#C8102E] text-white font-semibold">Sì</button>
              <button type="button" onClick={() => setConferma(false)}
                className="px-2 py-0.5 rounded border border-slate-300 text-slate-600">No</button>
            </span>
          ) : (
            <button type="button" onClick={() => setConferma(true)} title="Elimina chat definitivamente"
              data-testid={`chat-elimina-${chatId}`}
              className="text-slate-400 hover:text-[#C8102E] transition-colors duration-150"><Trash2 size={14} /></button>
          )}
          <button type="button" onClick={onClose} data-testid={`chat-chiudi-${chatId}`}
            className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
        </div>
      </div>
      <div className="max-h-[220px] overflow-y-auto px-3 py-2 space-y-2" data-testid={`chat-messaggi-${chatId}`}>
        {errore && <div className="text-xs text-slate-500 text-center py-2">{errore}</div>}
        {!chat && !errore && <div className="text-xs text-slate-400 text-center py-2">Caricamento…</div>}
        {chat?.messaggi?.map((m, i) => {
          const mio = m.da_email === me;
          return (
            <div key={i} className={`flex ${mio ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs shadow-sm
                ${mio ? "bg-[#0018A8] text-white" : "bg-white text-slate-800 border border-slate-200"}`}>
                {!mio && <div className="text-[10px] font-bold text-[#0018A8] mb-0.5">{m.da_nome}</div>}
                <div className="whitespace-pre-wrap break-words">{m.testo || <i>segnalazione senza nota</i>}</div>
                <div className={`text-[10px] mt-0.5 ${mio ? "text-blue-200" : "text-slate-400"}`}>{quando(m.il)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {chat && (
        <div className="flex items-end gap-2 px-3 py-2 border-t border-slate-200">
          <textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={1} autoFocus
            data-testid={`chat-testo-${chatId}`} placeholder="Scrivi un messaggio…"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); invia(); } }}
            className="flex-1 text-sm border border-slate-300 rounded-md px-2 py-1.5 min-h-[36px] max-h-[90px]
              bg-white focus:outline-none focus:border-[#0018A8]" />
          <button type="button" onClick={invia} disabled={invio || !testo.trim()} data-testid={`chat-invia-${chatId}`}
            className="h-9 w-9 grid place-items-center rounded-md bg-[#0018A8] text-white hover:bg-[#001480]
              disabled:opacity-50 transition-colors duration-150"><Send size={14} /></button>
        </div>
      )}
    </div>
  );
}
